// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
import type {MachineDefinition, TargetDevice, Point2} from '@core/machine'
import {portalMountsForEditing} from '../world/portalMounts'

/** Explicit authoring upgrade. Historical scored definitions are never upgraded by the loader. */
export function targetBackingForEditing(definition: MachineDefinition): MachineDefinition {
  definition=portalMountsForEditing(definition)
  definition=rampLockForEditing(definition)
  // Keep malformed drafts available for the validator's normal refusal path.
  if(!definition||!Array.isArray(definition.devices))return definition
  let changed=false
  let devices=definition.devices.map(d=>{
    if(d.kind==='captiveBall'&&d.cageContacts===undefined){changed=true;return {...d,cageContacts:true}}
    if((d.kind!=='dropTarget'&&d.kind!=='standupTarget')||d.solidBack!==undefined)return d
    changed=true;return {...d,solidBack:true}
  })
  const find=(id:string)=>definition.devices.find(d=>d.id===id) as TargetDevice|undefined
  const i=find('targetI'),o=find('targetO'),n=find('targetN')
  const original=(d:TargetDevice|undefined,x:number,y:number)=>d!==undefined&&d.kind==='dropTarget'
    &&Array.isArray(d.a)&&Array.isArray(d.b)&&d.a.length===2&&d.b.length===2
    &&Math.abs((d.a[0]+d.b[0])/2-x)<1e-8&&Math.abs((d.a[1]+d.b[1])/2-y)<1e-8
    &&Math.abs(d.b[0]-d.a[0]+.018)<1e-8&&Math.abs(d.b[1]-d.a[1]+.024)<1e-8
  // Repair only the exact original reversed Neon bank. Custom layouts retain their author's order.
  const reorder=definition.machineId==='neon-nova'&&original(i,-.109,-.052)&&original(o,-.1288,-.0784)&&original(n,-.1486,-.1048)
  if(!reorder)return changed?{...definition,devices}:definition
  devices=devices.map(d=>d.id==='targetI'?{...d,a:n!.a,b:n!.b}:d.id==='targetN'?{...d,a:i!.a,b:i!.b}:d)
  const shifts=new Map<string,Point2>()
  shifts.set(i!.lamp??i!.id,[(n!.a[0]+n!.b[0]-i!.a[0]-i!.b[0])/2,(n!.a[1]+n!.b[1]-i!.a[1]-i!.b[1])/2])
  const first=shifts.get(i!.lamp??i!.id)!
  shifts.set(n!.lamp??n!.id,[-first[0],-first[1]])
  const lamps=definition.lamps?.map(l=>{const delta=shifts.get(l.id);return delta&&l.position?{...l,position:[l.position[0]+delta[0],l.position[1]+delta[1]] as Point2}:l})
  let art=definition.art
  const program=art?.playfield?.['program']
  if(Array.isArray(program)){
    const labels=program.filter((o:Record<string,unknown>)=>o['op']==='label'&&['I','O','N'].includes(String(o['text'])))
    const stock=(text:string,x:number,y:number)=>labels.some((o:Record<string,unknown>)=>o['text']===text&&JSON.stringify(o['at'])===JSON.stringify([x,y]))
    if(labels.length===3&&stock('I',.36,.49)&&stock('O',.32,.47)&&stock('N',.28,.45)){
      const placements:Record<string,Point2>={I:[.2596967,.3314586],O:[.2981919,.3562055],N:[.3366871,.3809524]}
      art={...art!,playfield:{...art!.playfield,program:program.map((o:Record<string,unknown>)=>o['op']==='label'&&placements[String(o['text'])]?{...o,at:placements[String(o['text'])]}:o)}}
    }
  }
  return {...definition,devices,...(lamps===undefined?{}:{lamps}),...(art===undefined?{}:{art})}
}

/** Match shipped wiring only; keep custom edits and historical scored definitions untouched. */
function rampLockForEditing(definition:MachineDefinition):MachineDefinition {
  if(!definition||definition.machineId!=='neon-nova')return definition
  const wire=definition.switches?.find(s=>s.id==='wormholeRamp'&&s.device==='wormholeRamp')
  const rule=definition.rules?.rules?.find(r=>r.id==='wormholeLock'&&r.on==='switch:wormholeRamp')
  const action=rule?.do.find(a=>{const lock=a['lockBall'] as Record<string,unknown>|undefined;return lock?.['device']==='wormholeRamp'&&lock['virtual']===undefined})
  if(!wire||!rule||!action)return definition
  const devices=definition.devices.map(d=>d.kind==='spinner'&&d.id==='spinnerIon'&&d.a[0]===-.244&&d.a[1]===.1&&d.b[0]===-.188&&d.b[1]===.1?{...d,a:[-.18,-.02] as Point2,b:[-.124,-.02] as Point2}:d)
  return {...definition,devices,switches:definition.switches!.map(s=>s===wire?{...s,device:'wormholeRail'}:s),rules:{...definition.rules!,rules:definition.rules!.rules!.map(r=>r===rule?{...r,do:r.do.map(a=>a===action?{...a,lockBall:{...(a['lockBall'] as object),device:'wormholeRail',virtual:true}}:a)}:r)}}
}
